import { useState, useEffect, useCallback } from 'react';
import { contactsService, Contact } from '../services/contactsService';
import { useAuth } from '../context/AuthContext';

export const useContacts = () => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { contacts: data, error: fetchError } = await contactsService.getContacts(businessId || undefined);

      if (fetchError) {
        throw fetchError;
      }

      setContacts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    // Only fetch contacts when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchContacts();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setContacts([]);
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, fetchContacts]);

  const refreshContacts = () => {
    fetchContacts();
  };

  return {
    contacts,
    loading,
    error,
    refreshContacts,
  };
};

export const useContact = (id: string | null) => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    // Only fetch contact when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      const fetchContact = async () => {
        try {
          setLoading(true);
          setError(null);
          const { contact: data, error: fetchError } = await contactsService.getContactById(id, businessId || undefined);

          if (fetchError) {
            throw fetchError;
          }

          setContact(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch contact');
        } finally {
          setLoading(false);
        }
      };

      fetchContact();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setContact(null);
      setLoading(false);
    }
  }, [id, authLoading, isAuthenticated, businessId]);

  return {
    contact,
    loading,
    error,
  };
};